import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Quote, ArrowLeft, ArrowRight, Star } from 'lucide-react';
import { useTranslation } from '../../translations';

interface Testimonial {
  id: number;
  author: string;
  role: string;
  company: string;
  avatar: string;
  content: string;
}

export function PremiumTestimonials() {
  const { t, lang } = useTranslation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const testimonials: Record<string, Testimonial[]> = {
    NL: [
      {
        id: 1,
        author: "Sofie Lemmens",
        role: "Facility Coördinator",
        company: "Nationale retailketen",
        avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face",
        content: "Dankzij BizComfort verloopt elke interventie op onze locaties – van technische storingen tot dringende herstellingen – gestructureerd en transparant. Eén melding volstaat en we volgen alles real-time op in één platform. Geen gedoe meer met losse leveranciers of onduidelijke afspraken."
      },
      {
        id: 2,
        author: "Omar El Khattabi",
        role: "Service Operations Manager",
        company: "Producent van laadoplossingen",
        avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face",
        content: "Sinds we BizComfort gebruiken in onze installatieflow, verloopt de samenwerking tussen klant, installateur en onze planning vlekkeloos. Klanten kiezen zelf hun installatiemoment, de vakman ontvangt automatisch alle gegevens, en wij behouden het overzicht zonder manuele opvolging. Efficiëntie en klanttevredenheid zijn er merkbaar op vooruitgegaan."
      },
      {
        id: 3,
        author: "Hendrik Desmet",
        role: "Property Manager",
        company: "Vastgoedbeheerder van 30 gebouwen",
        avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face",
        content: "Voorheen verloren we veel tijd met het opvolgen van kleine onderhoudsvragen. Nu beheer ik al mijn gebouwen, dossiers en contactpersonen via één overzichtelijk platform. BizComfort zorgt voor snelheid, duidelijkheid en rust in onze dagdagelijkse werking."
      }
    ],
    FR: [
      {
        id: 1,
        author: "Sofie Lemmens",
        role: "Coordinatrice des installations",
        company: "Chaîne de magasins nationale",
        avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face",
        content: "Grâce à BizComfort, chaque intervention sur nos sites - des pannes techniques aux réparations urgentes - se déroule de manière structurée et transparente. Une seule notification suffit et nous suivons tout en temps réel sur une plateforme unique. Plus de tracas avec des fournisseurs disparates ou des accords peu clairs."
      },
      {
        id: 2,
        author: "Omar El Khattabi",
        role: "Responsable des opérations de service",
        company: "Fabricant de solutions de recharge",
        avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face",
        content: "Depuis que nous utilisons BizComfort dans notre flux d'installation, la collaboration entre le client, l'installateur et notre planification se déroule sans accroc. Les clients choisissent eux-mêmes leur moment d'installation, le technicien reçoit automatiquement toutes les informations, et nous gardons une vue d'ensemble sans suivi manuel. L'efficacité et la satisfaction client se sont nettement améliorées."
      },
      {
        id: 3,
        author: "Hendrik Desmet",
        role: "Gestionnaire immobilier",
        company: "Gestionnaire de 30 immeubles",
        avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face",
        content: "Auparavant, nous perdions beaucoup de temps à suivre les petites demandes de maintenance. Maintenant, je gère tous mes bâtiments, dossiers et contacts via une plateforme claire. BizComfort apporte rapidité, clarté et tranquillité dans notre fonctionnement quotidien."
      }
    ],
    EN: [
      {
        id: 1,
        author: "Sofie Lemmens",
        role: "Facility Coordinator",
        company: "National retail chain",
        avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&h=150&fit=crop&crop=face",
        content: "Thanks to BizComfort, every intervention at our locations - from technical failures to urgent repairs - proceeds in a structured and transparent manner. One notification is sufficient and we follow everything in real-time on a single platform. No more hassle with separate suppliers or unclear arrangements."
      },
      {
        id: 2,
        author: "Omar El Khattabi",
        role: "Service Operations Manager",
        company: "Charging solutions manufacturer",
        avatar: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=150&h=150&fit=crop&crop=face",
        content: "Since we started using BizComfort in our installation flow, the collaboration between customer, installer, and our planning runs smoothly. Customers choose their own installation time, the technician automatically receives all the information, and we maintain overview without manual follow-up. Efficiency and customer satisfaction have noticeably improved."
      },
      {
        id: 3,
        author: "Hendrik Desmet",
        role: "Property Manager",
        company: "Manager of 30 buildings",
        avatar: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=150&h=150&fit=crop&crop=face",
        content: "Previously, we lost a lot of time following up on small maintenance requests. Now I manage all my buildings, cases, and contacts through one clear platform. BizComfort provides speed, clarity, and peace in our daily operations."
      }
    ]
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials[lang].length);
    }, 6000);

    return () => clearInterval(timer);
  }, [lang, testimonials]);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0,
      scale: 0.8,
    })
  };

  const nextTestimonial = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials[lang].length);
  };

  const prevTestimonial = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials[lang].length) % testimonials[lang].length);
  };

  return (
    <section id="testimonials" className="relative py-16 md:py-24 bg-white overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0">
        {/* Subtle gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9]"></div>
        
        {/* Animated stars */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#3C5679]/20 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 0.8, 0.3],
            }}
            transition={{
              duration: 2 + Math.random() * 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">{t.testimonials.title}</h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            {t.testimonials.subtitle}
          </p>
        </div>

        {/* Main Testimonial Display */}
        <div className="relative max-w-4xl mx-auto mb-12">
          <div className="relative h-[550px] sm:h-[450px] md:h-[350px]">
            <AnimatePresence initial={false} custom={direction}>
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{
                  x: { type: "spring", stiffness: 300, damping: 30 },
                  opacity: { duration: 0.4 },
                  scale: { duration: 0.4 }
                }}
                className="absolute inset-0"
              >
                <div className="relative h-full bg-white rounded-2xl shadow-lg p-6 md:p-8 overflow-hidden border border-[#3C5679]/10">
                  <Quote className="absolute top-6 right-6 h-12 w-12 text-[#3C5679]/10" />

                  <div className="flex flex-col md:flex-row h-full gap-6 md:gap-8">
                    <div className="flex flex-col items-center md:items-start">
                      <div className="relative mb-4">
                        <div className="w-24 h-24 rounded-full overflow-hidden border-4 border-[#3C5679]/10 relative">
                          <img 
                            src={testimonials[lang][currentIndex].avatar} 
                            alt={testimonials[lang][currentIndex].author}
                            className="w-full h-full object-cover"
                          />
                          
                          {/* Animated stars around the avatar */}
                          {[...Array(5)].map((_, i) => (
                            <motion.div
                              key={i}
                              className="absolute w-2 h-2 bg-[#3C5679] rounded-full"
                              style={{
                                left: `${50 + 35 * Math.cos(2 * Math.PI * i / 5)}%`,
                                top: `${50 + 35 * Math.sin(2 * Math.PI * i / 5)}%`,
                              }}
                              animate={{
                                scale: [0, 1, 0],
                                opacity: [0, 1, 0],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                delay: i * 0.4,
                                ease: "easeInOut",
                              }}
                            />
                          ))}
                        </div>
                        
                        {/* Animated ring */}
                        <motion.div
                          className="absolute inset-0 border-2 border-[#3C5679]/20 rounded-full"
                          animate={{ 
                            scale: [1, 1.2, 1],
                            opacity: [0.5, 0, 0.5]
                          }}
                          transition={{ duration: 3, repeat: Infinity }}
                        />
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-1 text-center md:text-left">
                        {testimonials[lang][currentIndex].author}
                      </h3>
                      <p className="text-[#3C5679] mb-1 font-medium text-center md:text-left">
                        {testimonials[lang][currentIndex].role}
                      </p>
                      <p className="text-gray-500 mb-4 text-center md:text-left">
                        {testimonials[lang][currentIndex].company}
                      </p>
                      
                      {/* Star Rating */}
                      <div className="flex justify-center md:justify-start gap-1 mb-6">
                        {[...Array(5)].map((_, i) => (
                          <motion.div
                            key={i}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: i * 0.1, duration: 0.3 }}
                          >
                            <Star className="w-5 h-5 fill-[#3C5679] text-[#3C5679]" />
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 flex items-center">
                      <motion.blockquote 
                        className="text-base md:text-lg text-gray-700 leading-relaxed font-normal"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                      >
                        "{testimonials[lang][currentIndex].content}"
                      </motion.blockquote>
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Controls */}
          <div className="flex justify-center items-center gap-6 mt-8">
            <motion.button
              onClick={prevTestimonial}
              className="p-3 rounded-full bg-[#3C5679] text-white hover:bg-[#2A3F5F] transition-all shadow-md"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Previous testimonial"
            >
              <ArrowLeft className="w-5 h-5" />
            </motion.button>

            {/* Dots Indicator */}
            <div className="flex gap-3">
              {testimonials[lang].map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => {
                    setDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                  }}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentIndex 
                      ? 'bg-[#3C5679] scale-125' 
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  whileHover={{ scale: 1.2 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label={`Go to testimonial ${index + 1}`}
                />
              ))}
            </div>

            <motion.button
              onClick={nextTestimonial}
              className="p-3 rounded-full bg-[#3C5679] text-white hover:bg-[#2A3F5F] transition-all shadow-md"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              aria-label="Next testimonial"
            >
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </div>
    </section>
  );
}